// Hook to fetch the resolved effective entitlements for the current tenant.
// Combines tier ceiling (Layer 1) with tenant configuration (Layer 2).
// Used to conditionally adjust UI behaviour for tier-gated features.

import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { queryKeys } from '@/lib/api/queryKeys'

/** Resolved entitlement map returned by GET /entitlements. */
export interface Entitlements {
  // Boolean features (Layer 1 + Layer 2 resolved)
  confirmation_gates?: boolean
  secondary_signoff?: boolean
  assessment_overdue_notifications?: boolean
  parent_child_risks?: boolean
  compare_date_reporting?: boolean
  genai_api_available?: boolean

  // Numeric limits (-1 = unlimited)
  register_count?: number
  register_hierarchy_depth?: number
  category_hierarchy_depth?: number
  taxonomy_depth?: number
  impact_tables?: number
  genai_api_keys?: number

  // String values
  risk_appetite_granularity?: string
}

/**
 * Fetches the resolved entitlements for the current tenant.
 * Entitlements change rarely (tier upgrade or admin toggle), so a 5-minute
 * stale time avoids unnecessary refetches.
 */
export function useEntitlements() {
  const api = useApi()
  const { slug } = useTenant()

  return useQuery({
    queryKey: queryKeys.entitlements(slug),
    queryFn: () => api.get<Entitlements>('/entitlements'),
    staleTime: 5 * 60 * 1000,
  })
}
